import React, { useState, useEffect } from "react";
import Input from "sushiweb/InputTypes/Input";
import styles from "./CustomInputs.module.scss";
import Datepicker from "../datePicker/DatePicker";
import Image from "../../shared/Image/Image";
import { HTMLDiv } from "../HTMLDiv";
import moment from "moment";
import SpinnerCircular from "sushiweb/Spinner/SpinnerCircular";
import { red, green } from "sushiweb/tokens/color";
import { STATUS_VERIFY, STATUS_VERIFIED } from "../../../utils/constants/others";

function PanInputs(props) {
  const [isMobileWeb, setIsMobileWeb] = useState(false);

  // Hook to detect mobile web view specifically
  useEffect(() => {
    const checkIsMobileWeb = () => {
      const isMobile = window.innerWidth <= 992;
      const isWebBrowser = !window.navigator.userAgent.match(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i) || 
                           window.navigator.userAgent.match(/Chrome|Safari|Firefox|Edge/i);
      const isMobileWebDetected = isMobile && isWebBrowser;
      
      setIsMobileWeb(isMobileWebDetected);
    };
    
    checkIsMobileWeb();
    window.addEventListener('resize', checkIsMobileWeb);
    
    return () => {
      window.removeEventListener('resize', checkIsMobileWeb);
    };
  }, []);

  // Get mobile web specific popper configuration
  const getPopperConfig = () => {
    if (isMobileWeb) {
      return {
        strategy: 'absolute',
        modifiers: [
          {
            name: 'preventOverflow',
            enabled: false, // Completely disable to force bottom positioning
          },
          {
            name: 'flip',
            enabled: false, // Disable flip to prevent it from going to top
          },
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'hide',
            enabled: false, // Disable hide behavior
          },
        ],
      };
    }
    
    // Default configuration for desktop/native apps
    return {
      strategy: 'fixed',
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            rootBoundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
          },
        },
        {
          name: 'offset',
          options: {
            offset: [0, 4],
          },
        },
      ],
    };
  };

  // Icon handler for verification buttons - matching MultipleInputs pattern
  const getIconOnInput = (data, inputField, idx) => {
    return (
      <div 
        className="pointer" 
        style={{
          color: data?.text === STATUS_VERIFIED ? green.z500 : `${data?.color || red.z500}`
        }}
      >
        {data?.loaderVisible ? (
          <SpinnerCircular color={red.z500} size="small" />
        ) : data?.visible ? (
          <span onClick={() => handleIconButtonClick(data, inputField, idx)}>
            {data?.text}
          </span>
        ) : (
          <></>
        )}
      </div>
    );
  };

  // Handle icon button clicks - similar to MultipleInputs
  const handleIconButtonClick = (buttonData, inputField, idx) => {
    if (buttonData?.text === STATUS_VERIFIED) {
      return;
    }
    
    if (inputField?.value && props?.reset) {
      // Call parent reset function for verification
      props.reset(inputField.value, true, "PAN", STATUS_VERIFY, "");
    }
  };

  const getInputByInputType = (inputField, idx) => {
    if (inputField?.keyType === "input" && inputField?.visible) {
      return (
        <Input
          value={inputField?.value}
          onChange={(val) => props?.onChange(val, idx)}
          label={inputField?.key}
          icon={inputField?.Button ? getIconOnInput(inputField.Button, inputField, idx) : ""}
          className={
            !inputField?.editable
              ? `mb-15 ${styles.disabledInput} ${styles.genericInput}`
              : `mb-15 ${styles.genericInput}`
          }
          errorMsg={inputField?.error?.length ? (
            <HTMLDiv html={inputField?.error} />
          ) : ""}
          disabled={!inputField?.editable}
        />
      );
    } else if (inputField?.keyType === "calendar" && inputField?.visible) {
      return (
        <div
          className={`position-relative mb-15 ${styles.datePicker} ${styles.genericInput}`}
        >
          <Datepicker
            dateFormat="dd/MM/yyyy"
            showMonthDropdown
            showYearDropdown
            yearDropdownItemNumber={5}
            selected={inputField?.value}
            onChange={(val) => {
              props?.onChange(val, idx);
            }}
            onChangeRaw={(e) => e.preventDefault()}
            popperPlacement={isMobileWeb ? "bottom" : "bottom-start"}
            popperProps={getPopperConfig()}
            customInput={
              <Input
                className="position-relative"
                label={<span style={{ zIndex: '1000', position: 'relative', backgroundColor: '#fff', padding: '0 4px' }}>{inputField?.key}</span>}
                icon={
                  <Image
                    classname={`${styles.calendarIcon}`}
                    source={"/assets/images/datePickerIcon.png"}
                  />
                }
              />
            }
          />
          {inputField?.error?.length ? (
            <div className={`${styles.removeMargin}`}>
              <HTMLDiv html={inputField?.error} />
            </div>
          ) : (
            <></>
          )}
        </div>
      );
    }
  }

  return (
    <div className={`${styles.multipleInputScrollable}`}>
      {/* Entity details display - matching MultipleInputs pattern */}
      {props?.entityDetails?.BackgroundImage && 
       props?.entityDetails?.AvatarIcon && 
       props?.entityDetails?.Heading && 
       props?.entityDetails?.SubHeading && (
        <div className={`position-relative ${styles.detailsBox}`}>
          <Image classname={`wd-100`} source={props?.entityDetails?.BackgroundImage} />
          <Image 
            classname={`position-absolute ${styles.documentAvatar}`} 
            source={props?.entityDetails?.AvatarIcon} 
          />
          <div>
            <span className={`w-600 fs-18 position-absolute ${styles.documentDetails}`}>
              {props?.entityDetails?.Heading}
            </span>
            <span className={`w-400 fs-14 position-absolute ${styles.documentDetailsSub}`}>
              {props?.entityDetails?.SubHeading}
            </span>
          </div>
        </div>
      )}
      
      {/* Input fields rendering */}
      <div className="relative">
        {props?.values?.map((inputField, index) => {
          return inputField?.visible !== false ? (
            <div key={index}>
              {inputField?.Heading && (
                <div className="fs-20 w-800 mb-8">{inputField?.Heading}</div>
              )}
              {getInputByInputType(inputField, index)}
            </div>
          ) : null;
        })}
      </div>
    </div>
  );
}

export default PanInputs